import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React, { useState, useEffect } from "react";
import { useNotify, useRedirect, useTranslate, useUpdate } from "react-admin";
import ArgumentSelectButton from "../../debate/arguments/ArgumentSelectButton";
import StatusButton from "../../utils/StatusButton";
import ModerationEntryActivities from "../moderation_activities/ModerationEntryActivities";
import AuthorMessages from "./AuthorMessages";
import ModerableElement from "./ModerableElement";
import styles from "./ModerationEntry.module.scss";
import UserDetail from "./UserDetail";

const rejectReasons = [
	{
		value: "TOXICITY",
		label: "resources.moderation_entries.select.toxicity",
	},
	{
		value: "OBSCENE",
		label: "resources.moderation_entries.select.obscene",
	},
	{
		value: "HATE_SPEECH",
		label: "resources.moderation_entries.select.hate_speech",
	},
	{
		value: "INCOHERENT",
		label: "resources.moderation_entries.select.incoherent",
	},
	{
		value: "SPAM",
		label: "resources.moderation_entries.select.spam",
	},
	{
		value: "UNSUBSTANTIAL",
		label: "resources.moderation_entries.select.unsubstantial",
	},
];

export const ModerationEntry = ({
	moderationEntry,
	userId,
	onUpdate,
	onUpdateSelection,
	onReformulate,
}) => {
	const [moderatorNotes, setModeratorNotes] = useState(
		moderationEntry?.moderator_notes,
	);
	const translate = useTranslate();
	const notify = useNotify();
	const redirectTo = useRedirect();

	useEffect(() => {
		if (moderationEntry?.moderator_notes) {
			setModeratorNotes(moderationEntry.moderator_notes);
		}
	}, [moderationEntry]);

	useEffect(() => {
		window.addEventListener("keydown", handleKeyPress);

		return () => {
			window.removeEventListener("keydown", handleKeyPress);
		};
	}, []);

	const resetInputs = () => {
		setModeratorNotes(undefined);
	};

	const updatedObject = (status, reason = "") => {
		if (status === "pass") {
			return {
				...{ is_locked: false },
				...{ locked_by: null },
				...{ locked_at: null },
			};
		}
		return {
			...{ is_moderated: true },
			...(status && { status: status }),
			...(userId && { user_id: userId }),
			...(reason && { moderation_reason: reason }),
			...(moderatorNotes && { moderator_notes: moderatorNotes }),
			...(moderationEntry?.is_reported && { is_reported: false }),
		};
	};

	const handleUpdate = (previousElm) => {
		onUpdate?.(previousElm);
		resetInputs();
		window?.scrollTo(0, 0);
	};

	const handleKeyPress = (e) => {
		e.stopPropagation();
		if (e.ctrlKey && e.key === "p") {
			e.preventDefault();
			handleUpdate(true);
		}
	};

	const handleSelect = () => {
		if (moderationEntry?.moderable?.score !== 99) {
			selectObject();
			onUpdateSelection?.(99);
		} else {
			onUpdateSelection?.(50);
		}
	};

	const [selectObject] = useUpdate(
		"moderation_entries",
		{
			id: moderationEntry?.id,
			data: updatedObject("accepted"),
		},
		{
			onSuccess: () => {
				notify(
					translate(
						"resources.moderation_entries.notifications.moderation_success",
					),
					{ type: "info" },
				);
				handleUpdate();
			},
			onFailure: () => {
				notify(
					translate(
						"resources.moderation_entries.notifications.moderation_fail",
					),
					{ type: "warning" },
				);
			},
			mutationMode: "pessimistic",
		},
	);

	return (
		<div className={styles.container}>
			{!moderationEntry ? (
				<div className={styles.successContainer}>
					<Alert severity="error" className={styles.success}>
						{translate("resources.moderation_entries.label.moderation_error")}
					</Alert>
					<Button
						className={styles.successButton}
						onClick={() => redirectTo("/moderation_entries/lobby")}
					>
						{translate("resources.moderation_entries.label.reload")}
					</Button>
				</div>
			) : (
				<div className={styles.mainContainer}>
					<div className={styles.contentColumn}>
						<Card variant="outlined" className={styles.cardContainer}>
							<CardContent>
								<ModerableElement
									submitReformulation={(reformulation) =>
										onReformulate?.(reformulation)
									}
									moderable={moderationEntry?.moderable}
									moderableType={moderationEntry?.moderable_type}
									moderableScore={moderationEntry?.moderation_score}
								/>
							</CardContent>
						</Card>

						<Card variant="outlined" className={styles.userDetailContainer}>
							<Accordion>
								<AccordionSummary
									className={styles.expandable}
									expandIcon={<KeyboardArrowRightIcon />}
								>
									{translate(
										"resources.moderation_entries.label.moderation_history",
									)}
								</AccordionSummary>
								<AccordionDetails className={styles.expandableDetails}>
									<ModerationEntryActivities
										moderationEntryId={moderationEntry?.id}
									/>
								</AccordionDetails>
							</Accordion>
						</Card>

						{moderationEntry.moderable_type !==
							"SocialModerator::ModerableContent" && (
							<Card variant="outlined" className={styles.userDetailContainer}>
								<Accordion>
									<AccordionSummary
										className={styles.expandable}
										expandIcon={<KeyboardArrowRightIcon />}
									>
										{translate(
											"resources.moderation_entries.label.author_messages",
										)}
									</AccordionSummary>
									<AccordionDetails className={styles.expandableDetails}>
										<Box sx={{ overflowY: "scroll" }}>
											<AuthorMessages
												currentEntryId={
													moderationEntry?.moderable_type === "Message" &&
													moderationEntry.moderable.id
												}
												userId={
													moderationEntry?.moderable_type === "User"
														? moderationEntry?.moderable.id
														: moderationEntry?.moderable?.author?.id
												}
											/>
										</Box>
									</AccordionDetails>
								</Accordion>
							</Card>
						)}
					</div>
					<div className={styles.actionsColumn}>
						<Card variant="outlined" className={styles.actionsContainer}>
							<span className={styles.help}>
								<Tooltip
									title={translate(
										"resources.moderation_entries.label.keyboard_shortcuts",
									)}
								>
									<HelpOutlineIcon />
								</Tooltip>
							</span>
							<CardActions className={styles.cardActions}>
								<StatusButton
									resource="moderation_entries"
									acceptKey={"a"}
									record={moderationEntry}
									updateObject={updatedObject("accepted")}
									type="ACCEPT"
									onUpdate={() => handleUpdate()}
									noRedirect
								/>
								<Button
									variant="outlined"
									style={{ color: "rgba(91, 127, 241)" }}
									size="small"
									onClick={() => handleUpdate(true)}
								>
									<ArrowBackIcon
										color="rgba(91, 127, 241)"
										style={{
											paddingRight: "0.5em",
											color: "rgba(91, 127, 241)",
										}}
									/>
									{translate("resources.moderation_entries.label.previous")}
								</Button>
								<StatusButton
									resource="moderation_entries"
									passKey={"s"}
									record={moderationEntry}
									updateObject={updatedObject("pass")}
									type="SKIP"
									onUpdate={() => handleUpdate()}
									noRedirect
								/>
							</CardActions>
							<ArgumentSelectButton
								currentRecord={
									moderationEntry?.moderable_type === "Message" &&
									moderationEntry.moderable
								}
								noRedirect
								onSelect={handleSelect}
							/>
							<div className={styles.fieldsContainer}>
								<Typography variant="subtitle1" className={styles.rejectTitle}>
									{translate(
										"resources.moderation_entries.label.reject_content",
									)}
								</Typography>
								<FormControl fullWidth>
									<div>
										{rejectReasons.map((reason) => (
											<StatusButton
												key={reason.value}
												resource="moderation_entries"
												acceptKey={"a"}
												record={moderationEntry}
												updateObject={updatedObject("rejected", reason.value)}
												onUpdate={() => handleUpdate()}
												noRedirect
											>
												<Chip
													className={`${styles.rejectChip} ${
														moderationEntry.moderation_reason === reason.value
															? styles.selectedChip
															: ""
													}`}
													label={translate(reason.label)}
												/>
											</StatusButton>
										))}

										<StatusButton
											resource="moderation_entries"
											acceptKey={"a"}
											record={moderationEntry}
											updateObject={updatedObject("rejected", "")}
											onUpdate={() => handleUpdate()}
											noRedirect
										>
											<Chip
												className={styles.undefinedChip}
												label={translate(
													"resources.moderation_entries.select.undefined",
												)}
											/>
										</StatusButton>
									</div>
								</FormControl>
							</div>
							<TextField
								className={`${styles.notesField} ${styles.textField}`}
								onChange={(e) => setModeratorNotes(e.target.value)}
								value={moderatorNotes || ""}
								variant="outlined"
								multiline
								fullWidth
								label={translate(
									"resources.moderation_entries.label.entry_notes",
								)}
							/>
						</Card>
						{moderationEntry.moderable_type !==
							"SocialModerator::ModerableContent" && (
							<Card variant="outlined" className={styles.userInfoContainer}>
								<UserDetail entry={moderationEntry} />
							</Card>
						)}
					</div>
				</div>
			)}
		</div>
	);
};
